// get pricing for the selections in a modifier group, with discounts from `defaultOptionsSubstitutionPricing`
import { SelectionInput } from 'src/apollo/onlineOrdering'
import {
  CartOrder,
  CompletedOrder,
  IModifierGroup,
  ISelectionModifierGroup,
  Modifier,
  ModifierGroupData,
  SelectedModifier,
  SelectionModifier,
  Selections,
  WrappedModifier
} from 'public/components/online_ordering/types'

export const getModifierThumbSrc = (
  modifier: Pick<Modifier, 'imageUrls'>
): string | undefined =>
  modifier.imageUrls?.xs ||
  modifier.imageUrls?.small ||
  modifier.imageUrls?.medium ||
  undefined

const getSelectionsPrice = (
  selections: Selections,
  modifierGroupData: ModifierGroupData,
  modifierGroupCredits: { [key: string]: number },
  selectedMods: { [key: string]: string }
): number => {
  return Object.entries(selections)
    .map<[number | undefined, number]>(([groupGuid, item]) => {
      let index = 0
      return [
        modifierGroupCredits[groupGuid],
        Object.values(item)
          .map((mod) => {
            const { price, newIndex } = getModifierPrice(
              mod,
              groupGuid,
              modifierGroupData,
              modifierGroupCredits,
              selectedMods,
              index
            )
            index = newIndex
            return price
          })
          .reduce((a, b) => a + b, 0)
      ]
    })
    .reduce((price, [credits, modPrice]) => {
      return price + (credits ? Math.max(modPrice - credits, 0) : modPrice)
    }, 0)
}
// Get the price for a specific modifier and any submodifiers, taking `defaultOptionsChargePrice` into account.
// Also returns the new index for the next call which takes into account the quantity of the previous mod
export const getModifierPrice = (
  mod: SelectedModifier,
  groupGuid: string,
  modifierGroupData: ModifierGroupData,
  modifierGroupCredits: { [key: string]: number },
  selectedMods: { [key: string]: string },
  index: number
): { price: number; newIndex: number } => {
  const baseQuantity = mod.quantity || 1

  const pricingRules = modifierGroupData[groupGuid]?.pricingRules
  const sizeModGuid = pricingRules?.sizeSpecificPricingGuid // modifier to check for size sequence pricing
  const sizeSequencePrices = pricingRules?.sizeSequencePricingRules
  const defaultSequencePrices = sizeSequencePrices?.[0]?.sequencePrices
  const sequencePrices =
    !sizeModGuid || !sizeSequencePrices || sizeSequencePrices.length === 1
      ? defaultSequencePrices // sequence pricing
      : sizeSequencePrices.find(
          (size) => size.sizeGuid === selectedMods[sizeModGuid]
        )?.sequencePrices || defaultSequencePrices // size sequence pricing

  // if default options aren't charged, treat the quantity as one less than it is
  const quantity =
    mod.isDefault &&
    modifierGroupData[groupGuid]?.defaultOptionsChargePrice === 'NO'
      ? baseQuantity - 1
      : baseQuantity
  const thisPrice = mod.price
    ? quantity * mod.price
    : sequencePrices?.length
    ? new Array(quantity)
        .fill('')
        .reduce(
          (sum, _, qIndex) =>
            sum +
            sequencePrices[Math.min(index + qIndex, sequencePrices.length - 1)]
              ?.price,
          0
        )
    : 0

  const newIndex = index + quantity

  if (Object.keys(mod.modifierGroups).length === 0) {
    return {
      price: thisPrice,
      newIndex
    }
  }

  return {
    price:
      thisPrice +
      getSelectionsPrice(
        mod.modifierGroups,
        modifierGroupData,
        modifierGroupCredits,
        selectedMods
      ) *
        baseQuantity,
    newIndex
  }
}
// get the price for a top-level modifier
// Note: this can return a price less than the base price for an item--the caller must take care
// to ensure that the final presented price is floored to that base price
export const getWrappedModifierPrice = (
  mod: WrappedModifier,
  modifierGroupData: ModifierGroupData,
  modifierGroupCredits: { [key: string]: number },
  selectedMods: { [key: string]: string }
): number => {
  const thisPrice = mod.price || 0
  const quantity = mod.quantity || 1
  if (Object.keys(mod.modifierGroups).length === 0) {
    return thisPrice * quantity
  }

  return (
    (thisPrice +
      getSelectionsPrice(
        mod.modifierGroups,
        modifierGroupData,
        modifierGroupCredits,
        selectedMods
      )) *
    quantity
  )
}
const getSelectionsNames = (selections: Selections): string[] => {
  return Object.values(selections)
    .flatMap((items) => Object.values(items))
    .flatMap((item) => [item.name, ...getSelectionsNames(item.modifierGroups)])
    .filter(Boolean) as string[]
}
export const getSubselectionNames = (
  mod: WrappedModifier,
  groupGuid: string,
  itemGuid: string
): string[] => {
  const subGroups =
    mod.modifierGroups[groupGuid]?.[itemGuid]?.modifierGroups || {}

  return Object.values(subGroups)
    .flatMap((items) => Object.values(items))
    .flatMap((item) => [item.name, ...getSelectionsNames(item.modifierGroups)])
    .filter(Boolean) as string[]
}
const selectedModifierToSelectionInput = (
  item: SelectedModifier,
  quantity: number,
  parentItemGroupGuid: string
): SelectionInput => {
  return {
    itemGuid: item.itemGuid,
    itemGroupGuid: item.itemGroupGuid || parentItemGroupGuid,
    quantity,
    modifierGroups: Object.entries(item.modifierGroups).map(
      ([guid, itemMap]) => ({
        guid,
        modifiers: Object.values(itemMap).flatMap((item) => {
          const input = selectedModifierToSelectionInput(
            item,
            quantity,
            parentItemGroupGuid
          )
          return new Array(item.quantity).fill(input)
        })
      })
    )
  }
}
export const modifierToSelectionInput = (
  item: WrappedModifier
): SelectionInput => {
  const quantity =
    item.quantity && !item.usesFractionalQuantity ? item.quantity : 1

  return {
    itemGuid: item.modifier.itemGuid,
    itemGroupGuid: item.groupGuid,
    itemMasterId: item.masterId,
    quantity: quantity,
    specialInstructions: item.specialInstructions,
    fractionalQuantity:
      item.usesFractionalQuantity && item.unitOfMeasure
        ? {
            unitOfMeasure: item.unitOfMeasure,
            quantity: item.quantity || 1
          }
        : null,
    modifierGroups: Object.entries(item.modifierGroups).map(
      ([guid, itemMap]) => ({
        guid,
        modifiers: Object.values(itemMap).flatMap((modifier) => {
          const input = selectedModifierToSelectionInput(
            modifier,
            quantity,
            item.groupGuid
          )
          return new Array(modifier.quantity).fill(input)
        })
      })
    )
  }
}
export const collapseModifierGroups = (
  groups: IModifierGroup[]
): Selections => {
  if (Object.keys(groups).length === 0) {
    return {}
  }

  return groups.reduce(
    (groupMap, group) => ({
      ...groupMap,
      [group.guid]: group.modifiers
        .filter((mod) => mod.isDefault)
        .reduce(
          (itemMap, item) => ({
            ...itemMap,
            [item.itemGuid]: {
              itemGuid: item.itemGuid,
              itemGroupGuid: item.itemGroupGuid,
              name: item.name,
              isDefault: item.isDefault || false,
              quantity: 1,
              price: item.price,
              modifierGroups: item.modifierGroups
                ? collapseModifierGroups(item.modifierGroups)
                : []
            } as SelectedModifier
          }),
          {}
        )
    }),
    {} as Selections
  )
}
const collapseSelectionModifierGroups = (
  groups: ISelectionModifierGroup[]
): Selections => {
  if (Object.keys(groups).length === 0) {
    return {}
  }

  return groups.reduce(
    (groupMap, group) => ({
      ...groupMap,
      [group.guid]: group.modifiers
        .filter((mod) => mod.selected)
        .reduce(
          (itemMap, item) => ({
            ...itemMap,
            [item.itemGuid]: {
              itemGuid: item.itemGuid,
              itemGroupGuid: item.itemGroupGuid,
              name: item.name,
              isDefault: item.isDefault,
              quantity: item.quantity ? item.quantity : 1,
              price: item.price,
              modifierGroups: item.modifierGroups
                ? collapseSelectionModifierGroups(item.modifierGroups)
                : []
            }
          }),
          {}
        )
    }),
    {}
  )
}
export const collapseModifier = (
  modifier?: Omit<SelectionModifier, '__typename'>
): WrappedModifier => {
  return {
    groupGuid: modifier?.itemGroupGuid || 'root',
    action: 'replace',
    modifier: modifier as Modifier,
    modifierGroups: modifier?.modifierGroups
      ? collapseSelectionModifierGroups(modifier.modifierGroups)
      : {},
    isRoot: true,
    price: modifier?.price || 0,
    quantity: 1
  }
}

/**
 * Marks modifiers as selected based on what's in the cart.
 * Takes flat cart modifiers and structured modifier groups, returns the groups
 * with correct selected flags.
 */
export const markSelectedModifiers = (
  cartModifiers: { optionGroupGuid?: string | null; name?: string | null }[],
  modifierGroups: ISelectionModifierGroup[]
): ISelectionModifierGroup[] => {
  // Build a set of (groupGuid, modifierName) pairs for modifiers in the cart
  const selectedModifiers = new Set(
    cartModifiers.map((mod) => {
      return `${mod.optionGroupGuid}:${mod.name}`
    })
  )

  // Mark modifiers as selected if they're in the cart
  return modifierGroups.map((group) => ({
    ...group,
    modifiers: group.modifiers.map((mod) => ({
      ...mod,
      selected: selectedModifiers.has(`${group.guid}:${mod.name}`)
    }))
  }))
}

export const calculateSubtotal = (order: CartOrder | CompletedOrder) =>
  order?.preDiscountItemsSubtotal || 0
